import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Button,
  Dropzone,
  Input,
  Range,
  Select,
  SvgIcon,
  Typography,
  clsx,
  globalStyles,
} from "@gemlightbox/core-kit";

import { useStores } from "src/hooks";
import { CommandAction, Mat3, Vec2, Webgl2New } from "src/image-editor-core";
import { LocaleCodeTypes } from "src/store/locale/locale-generated.store.types";
import { unsavedChangesNotification } from "src/utils";

import { ReactComponent as UploadSVG } from "src/external-ts/assets/images/upload-cloud-grey.svg";
import { ReactComponent as TrashSVG } from "src/external-ts/assets/images/trash-grey.svg";
import { editMediaStore } from "../../edit-media.store";
import styles from "./watermark.module.css";

type WatermarkMode = "text" | "logo";
type TextWatermarkAlignment = "left" | "center" | "right";

type TextWatermark = {
  id: string;
  text: string;
  position: {
    x: number;
    y: number;
  };
  dimensions: {
    width: number;
    height: number;
  };
  rotationAngle: number;
  font: string;
  size: string;
  weight: string;
  alignment: TextWatermarkAlignment;
  color: string;
  opacity: number;
};

type LogoWatermark = {
  id: string;
  name: string;
  imageUrl: string;
  opacity: number;
};

const defaultTextWatermarks: TextWatermark[] = [];
const defaultLogoWatermarks: LogoWatermark[] = [];
const defaultDraftWatermark: TextWatermark = {
  id: "",
  text: "",
  position: {
    x: 0,
    y: 0,
  },
  dimensions: {
    width: 0,
    height: 0,
  },
  rotationAngle: 0,
  font: "Source Sans",
  size: "24",
  weight: "Semi Bold",
  alignment: "center",
  color: "#1A1A1A",
  opacity: 100,
};

type FontOption = {
  label: string;
  value: string;
  fallbacks: string[];
};

const fontOptions: FontOption[] = [
  {
    label: "Source Sans",
    value: "Source Sans",
    fallbacks: ["Source Sans 3", "Source Sans Pro", "Arial", "Helvetica", "sans-serif"],
  },
  {
    label: "Inter",
    value: "Inter",
    fallbacks: ["Segoe UI", "Roboto", "Helvetica Neue", "Arial", "sans-serif"],
  },
  {
    label: "Playfair Display",
    value: "Playfair Display",
    fallbacks: ["Georgia", "Times New Roman", "serif"],
  },
  {
    label: "Roboto",
    value: "Roboto",
    fallbacks: ["Arial", "Helvetica", "sans-serif"],
  },
  {
    label: "Arial",
    value: "Arial",
    fallbacks: ["Helvetica", "Liberation Sans", "sans-serif"],
  },
  {
    label: "Helvetica",
    value: "Helvetica",
    fallbacks: ["Helvetica Neue", "Arial", "Liberation Sans", "sans-serif"],
  },
  {
    label: "Segoe UI",
    value: "Segoe UI",
    fallbacks: ["Roboto", "Helvetica Neue", "Arial", "sans-serif"],
  },
  {
    label: "Aptos",
    value: "Aptos",
    fallbacks: ["Calibri", "Segoe UI", "Arial", "sans-serif"],
  },
  {
    label: "Calibri",
    value: "Calibri",
    fallbacks: ["Aptos", "Segoe UI", "Arial", "sans-serif"],
  },
  {
    label: "Verdana",
    value: "Verdana",
    fallbacks: ["Geneva", "Tahoma", "sans-serif"],
  },
  {
    label: "Tahoma",
    value: "Tahoma",
    fallbacks: ["Verdana", "Geneva", "sans-serif"],
  },
  {
    label: "Trebuchet MS",
    value: "Trebuchet MS",
    fallbacks: ["Verdana", "Arial", "sans-serif"],
  },
  {
    label: "Gill Sans",
    value: "Gill Sans",
    fallbacks: ["Trebuchet MS", "Calibri", "Arial", "sans-serif"],
  },
  {
    label: "Futura",
    value: "Futura",
    fallbacks: ["Avenir Next", "Avenir", "Century Gothic", "Arial", "sans-serif"],
  },
  {
    label: "Avenir",
    value: "Avenir",
    fallbacks: ["Avenir Next", "Futura", "Century Gothic", "Arial", "sans-serif"],
  },
  {
    label: "Century Gothic",
    value: "Century Gothic",
    fallbacks: ["Futura", "Avenir", "Arial", "sans-serif"],
  },
  {
    label: "Franklin Gothic Medium",
    value: "Franklin Gothic Medium",
    fallbacks: ["Arial Narrow", "Arial", "sans-serif"],
  },
  {
    label: "Georgia",
    value: "Georgia",
    fallbacks: ["Times New Roman", "Times", "serif"],
  },
  {
    label: "Times New Roman",
    value: "Times New Roman",
    fallbacks: ["Times", "Liberation Serif", "serif"],
  },
  {
    label: "Palatino",
    value: "Palatino",
    fallbacks: ["Palatino Linotype", "Book Antiqua", "Georgia", "serif"],
  },
  {
    label: "Garamond",
    value: "Garamond",
    fallbacks: ["Baskerville", "Georgia", "serif"],
  },
  {
    label: "Baskerville",
    value: "Baskerville",
    fallbacks: ["Libre Baskerville", "Georgia", "serif"],
  },
  {
    label: "Didot",
    value: "Didot",
    fallbacks: ["Bodoni 72", "Bodoni MT", "Georgia", "serif"],
  },
  {
    label: "Courier New",
    value: "Courier New",
    fallbacks: ["Courier", "Liberation Mono", "monospace"],
  },
  {
    label: "Menlo",
    value: "Menlo",
    fallbacks: ["Monaco", "Consolas", "Courier New", "monospace"],
  },
  {
    label: "Consolas",
    value: "Consolas",
    fallbacks: ["Menlo", "Monaco", "Courier New", "monospace"],
  },
  {
    label: "Comic Sans MS",
    value: "Comic Sans MS",
    fallbacks: ["Comic Sans", "Chalkboard SE", "Marker Felt", "cursive"],
  },
  {
    label: "Brush Script MT",
    value: "Brush Script MT",
    fallbacks: ["Segoe Script", "Apple Chancery", "cursive"],
  },
  {
    label: "Apple Chancery",
    value: "Apple Chancery",
    fallbacks: ["Segoe Script", "Brush Script MT", "cursive"],
  },
  {
    label: "Segoe Script",
    value: "Segoe Script",
    fallbacks: ["Apple Chancery", "Brush Script MT", "cursive"],
  },
  {
    label: "PingFang SC",
    value: "PingFang SC",
    fallbacks: ["Microsoft YaHei", "Noto Sans CJK SC", "Hiragino Sans GB", "sans-serif"],
  },
  {
    label: "Microsoft YaHei",
    value: "Microsoft YaHei",
    fallbacks: ["PingFang SC", "Noto Sans CJK SC", "SimHei", "sans-serif"],
  },
  {
    label: "SimSun",
    value: "SimSun",
    fallbacks: ["Songti SC", "Noto Serif CJK SC", "serif"],
  },
  {
    label: "Hiragino Sans",
    value: "Hiragino Sans",
    fallbacks: ["Yu Gothic", "Meiryo", "Noto Sans CJK JP", "sans-serif"],
  },
  {
    label: "Meiryo",
    value: "Meiryo",
    fallbacks: ["Yu Gothic", "Hiragino Sans", "Noto Sans CJK JP", "sans-serif"],
  },
  {
    label: "Yu Gothic",
    value: "Yu Gothic",
    fallbacks: ["Meiryo", "Hiragino Sans", "Noto Sans CJK JP", "sans-serif"],
  },
  {
    label: "Apple SD Gothic Neo",
    value: "Apple SD Gothic Neo",
    fallbacks: ["Malgun Gothic", "Noto Sans CJK KR", "sans-serif"],
  },
  {
    label: "Malgun Gothic",
    value: "Malgun Gothic",
    fallbacks: ["Apple SD Gothic Neo", "Noto Sans CJK KR", "sans-serif"],
  },
  {
    label: "Arial Unicode MS",
    value: "Arial Unicode MS",
    fallbacks: ["Noto Sans", "Arial", "sans-serif"],
  },
  {
    label: "Noto Sans",
    value: "Noto Sans",
    fallbacks: ["Arial Unicode MS", "Arial", "sans-serif"],
  },
  {
    label: "Noto Serif",
    value: "Noto Serif",
    fallbacks: ["Georgia", "Times New Roman", "serif"],
  },
  {
    label: "Noto Naskh Arabic",
    value: "Noto Naskh Arabic",
    fallbacks: ["Geeza Pro", "Arial", "sans-serif"],
  },
  {
    label: "Geeza Pro",
    value: "Geeza Pro",
    fallbacks: ["Noto Naskh Arabic", "Arial", "sans-serif"],
  },
  {
    label: "Noto Sans Hebrew",
    value: "Noto Sans Hebrew",
    fallbacks: ["Arial Hebrew", "Arial", "sans-serif"],
  },
  {
    label: "Arial Hebrew",
    value: "Arial Hebrew",
    fallbacks: ["Noto Sans Hebrew", "Arial", "sans-serif"],
  },
  {
    label: "Noto Sans Devanagari",
    value: "Noto Sans Devanagari",
    fallbacks: ["Kohinoor Devanagari", "Mangal", "Arial", "sans-serif"],
  },
  {
    label: "Noto Sans Thai",
    value: "Noto Sans Thai",
    fallbacks: ["Thonburi", "Leelawadee UI", "Arial", "sans-serif"],
  },
  {
    label: "System Sans",
    value: "System Sans",
    fallbacks: [
      "-apple-system",
      "BlinkMacSystemFont",
      "Segoe UI",
      "Roboto",
      "Helvetica Neue",
      "Arial",
      "sans-serif",
    ],
  },
  {
    label: "System Serif",
    value: "System Serif",
    fallbacks: ["Georgia", "Times New Roman", "Times", "serif"],
  },
  {
    label: "System Monospace",
    value: "System Monospace",
    fallbacks: ["SFMono-Regular", "Menlo", "Consolas", "Liberation Mono", "monospace"],
  },
];
const weightOptions = ["Regular", "Medium", "Semi Bold", "Bold"] as const;
type WeightOption = (typeof weightOptions)[number];
const alignmentOptions: TextWatermarkAlignment[] = ["left", "center", "right"];
const fontSelectOptions = fontOptions.map(({ label, value }) => ({
  label,
  value,
  indelible: true,
}));
const weightLocaleKeys: Record<WeightOption, LocaleCodeTypes> = {
  Regular: '["edit-media"].inspector.watermark.regular',
  Medium: '["edit-media"].inspector.watermark.medium',
  "Semi Bold": '["edit-media"].inspector.watermark["semi-bold"]',
  Bold: '["edit-media"].inspector.watermark.bold',
};
const alignmentLocaleKeys: Record<TextWatermarkAlignment, string> = {
  left: '["edit-media"].inspector.watermark.alignment.left',
  center: '["edit-media"].inspector.watermark.alignment.center',
  right: '["edit-media"].inspector.watermark.alignment.right',
};
const hexColorRegExp = /^#[0-9a-f]{6}$/i;
const textCanvasPadding = 8;
const initialWatermarkTopOffset = 64;
const fontSizeMin = 12;
const fontSizeMax = 160;
const fontSizeStep = 1;
const fontWeightMap: Record<string, number> = {
  Regular: 400,
  Medium: 500,
  "Semi Bold": 600,
  Bold: 700,
};
const genericFontFamilies = new Set(["cursive", "fantasy", "monospace", "sans-serif", "serif"]);

const getWatermarkPreviewScale = () => {
  const renderer = editMediaStore.renderer;
  const cameraScale = renderer?.camera?.scale ?? 1;
  const devicePixelRatio = typeof window === "undefined" ? 1 : window.devicePixelRatio || 1;
  const previewScale = cameraScale / devicePixelRatio;

  return Number.isFinite(previewScale) && previewScale > 0 ? previewScale : 1;
};

const toWatermarkSourcePx = (displayPx: number) => {
  return displayPx / getWatermarkPreviewScale();
};

const clampFontSize = (value: number) => {
  if (!Number.isFinite(value)) return Number(defaultDraftWatermark.size);

  return Math.min(fontSizeMax, Math.max(fontSizeMin, Math.round(value)));
};

const getTextWatermarkFont = (watermark: TextWatermark, sourceFontSize: number) => {
  const fontSize = Math.max(1, sourceFontSize);
  const fontWeight = fontWeightMap[watermark.weight] ?? fontWeightMap[defaultDraftWatermark.weight];
  const selectedFontOption = fontOptions.find((option) => option.value === watermark.font);
  const fontFamilies = [
    watermark.font,
    ...(selectedFontOption?.fallbacks ?? ["Arial", "sans-serif"]),
  ];
  const fontFamily = fontFamilies
    .filter((font, index, fonts) => font && fonts.indexOf(font) === index)
    .map((font) => {
      if (genericFontFamilies.has(font)) return font;
      return `"${font.replace(/"/g, '\\"')}"`;
    })
    .join(", ");

  return `${fontWeight} ${fontSize}px ${fontFamily}`;
};

const createTextWatermarkCanvas = (watermark: TextWatermark) => {
  const displayFontSize = Number(watermark.size) || Number(defaultDraftWatermark.size);
  const sourceFontSize = Math.max(1, toWatermarkSourcePx(displayFontSize));
  const lineHeight = Math.ceil(sourceFontSize * 1.2);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) return null;

  const lines = watermark.text.split(/\r?\n/).filter((line) => line.length);
  const textLines = lines.length ? lines : [watermark.text];
  const font = getTextWatermarkFont(watermark, sourceFontSize);
  context.font = font;

  const textMetrics = textLines.map((line) => context.measureText(line));
  const measuredWidth = Math.max(...textMetrics.map((metrics) => Math.ceil(metrics.width)));
  const measuredAscent = Math.max(
    ...textMetrics.map((metrics) => metrics.actualBoundingBoxAscent || sourceFontSize * 0.8),
  );
  const measuredDescent = Math.max(
    ...textMetrics.map((metrics) => metrics.actualBoundingBoxDescent || sourceFontSize * 0.2),
  );
  const measuredTextHeight = measuredAscent + measuredDescent;
  const lineVerticalOffset = Math.max(0, lineHeight - measuredTextHeight) / 2;
  const width = Math.max(1, measuredWidth + textCanvasPadding * 2);
  const height = Math.max(1, lineHeight * textLines.length + textCanvasPadding * 2);

  canvas.width = width;
  canvas.height = height;

  context.font = font;
  context.fillStyle = hexColorRegExp.test(watermark.color)
    ? watermark.color
    : defaultDraftWatermark.color;
  context.textAlign = watermark.alignment;
  context.textBaseline = "alphabetic";
  textLines.forEach((line, index) => {
    const textPositionX =
      watermark.alignment === "left"
        ? textCanvasPadding
        : watermark.alignment === "right"
        ? width - textCanvasPadding
        : width / 2;

    context.fillText(
      line,
      textPositionX,
      textCanvasPadding + index * lineHeight + lineVerticalOffset + measuredAscent,
    );
  });

  return canvas;
};

const getComponentCenterPosition = (component: Webgl2New.ImageAdjustRectComponent) => {
  return component.transform.translation
    .clone()
    .add(component.transform.size.getHalf().rotateUnitDegrees(-component.transform.degrees));
};

const getSyncedWatermarkFromComponent = (
  watermark: TextWatermark,
  component: Webgl2New.ImageAdjustRectComponent,
): TextWatermark => {
  const position = getComponentCenterPosition(component);

  return {
    ...watermark,
    position: {
      x: position.x,
      y: position.y,
    },
    rotationAngle: component.transform.degrees,
  };
};

export const Watermark: React.FC = observer(() => {
  const { localeStore } = useStores();

  const [mode, setMode] = useState<WatermarkMode>("text");
  const [textWatermarks, setTextWatermarks] = useState(defaultTextWatermarks);
  const [logoWatermarks, setLogoWatermarks] = useState(defaultLogoWatermarks);
  const [draftWatermark, setDraftWatermark] = useState(defaultDraftWatermark);
  const [selectedWatermarkId, setSelectedWatermarkId] = useState("");
  const [selectedLogoWatermarkId, setSelectedLogoWatermarkId] = useState("");
  const focusedInputValueRef = useRef("");
  const textInputRef = useRef<(HTMLInputElement & HTMLTextAreaElement) | null>(null);
  const initialCommandHistoryRef = useRef(editMediaStore.renderer.commands.currentHistory);
  const watermarkComponentsRef = useRef(new Map<string, Webgl2New.ImageAdjustRectComponent>());

  const selectedWatermarkFromList = useMemo(() => {
    return textWatermarks.find((watermark) => watermark.id === selectedWatermarkId);
  }, [selectedWatermarkId, textWatermarks]);
  const selectedWatermark = selectedWatermarkFromList ?? draftWatermark;
  const selectedLogoWatermark = useMemo(() => {
    return logoWatermarks.find((watermark) => watermark.id === selectedLogoWatermarkId);
  }, [logoWatermarks, selectedLogoWatermarkId]);
  const [isAlignmentMenuOpen, setIsAlignmentMenuOpen] = useState(false);
  const alignmentMenuRef = useRef<HTMLDivElement | null>(null);
  const selectedFontSize = clampFontSize(Number(selectedWatermark.size));
  const isEditingDraft = !selectedWatermarkFromList;
  const canAddTextWatermark = isEditingDraft && !!draftWatermark.text.trim();
  const canApplyWatermark = textWatermarks.length > 0 || logoWatermarks.length > 0;
  const hasPendingWatermarkChanges =
    canApplyWatermark ||
    !!draftWatermark.text.trim() ||
    editMediaStore.renderer.commands.currentHistory > initialCommandHistoryRef.current;
  const weightSelectOptions = useMemo(() => {
    return weightOptions.map((option) => ({
      label: localeStore.t(weightLocaleKeys[option]),
      value: option,
      indelible: true,
    }));
  }, [localeStore, localeStore.currentLocaleCode]);
  const getDeleteLogoLabel = (name: string) => {
    return localeStore
      .t('["edit-media"].inspector.watermark["delete-logo"]')
      .replace("{{name}}", name);
  };
  const getAlignmentLabel = (alignment: TextWatermarkAlignment) => {
    const labelMap: Record<TextWatermarkAlignment, string> = {
      left: "Left",
      center: "Center",
      right: "Right",
    };

    return localeStore.t(alignmentLocaleKeys[alignment] as any, labelMap[alignment]);
  };
  const getAlignmentIconClassName = (alignment: TextWatermarkAlignment) => {
    return clsx(styles.alignmentIcon, {
      [styles.alignLeft]: alignment === "left",
      [styles.alignCenter]: alignment === "center",
      [styles.alignRight]: alignment === "right",
    });
  };
  const renderAlignmentIcon = (alignment: TextWatermarkAlignment) => (
    <span className={getAlignmentIconClassName(alignment)} aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );
  const getAlignmentButtonClassName = (alignment: TextWatermarkAlignment, isSelected = false) => {
    return clsx(styles.alignmentButton, getAlignmentIconClassName(alignment), {
      [styles.active]: isSelected,
    });
  };

  const focusTextInput = useCallback(() => {
    window.requestAnimationFrame(() => {
      const input = textInputRef.current;
      if (!input) return;

      input.focus();
      const textLength = input.value?.length ?? 0;
      input.setSelectionRange?.(textLength, textLength);
    });
  }, []);

  useEffect(() => {
    const mainImageComponent = editMediaStore.mainImageComponent;
    if (!mainImageComponent) return;

    const renderer = editMediaStore.renderer;
    const initialPointerEvents = mainImageComponent.style.pointerEvents;
    const initialIsLogoWatermarkEdit = renderer.isLogoWatermarkEdit;

    mainImageComponent.style.pointerEvents = "none";
    renderer.isLogoWatermarkEdit = true;

    if (renderer.selectedComponent === (mainImageComponent as any)) {
      renderer.selectComponent(null);
    }

    return () => {
      mainImageComponent.style.pointerEvents = initialPointerEvents;
      renderer.isLogoWatermarkEdit = initialIsLogoWatermarkEdit;
      renderer.selectionComponent.removeAlignmentLines();
    };
  }, []);

  useEffect(() => {
    if (!isAlignmentMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (alignmentMenuRef.current?.contains(event.target as Node)) return;
      setIsAlignmentMenuOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isAlignmentMenuOpen]);

  useEffect(() => {
    const renderer = editMediaStore.renderer;
    if (!renderer?.canvas) return;

    const getWatermarkEntry = (
      component: unknown,
    ): [string, Webgl2New.ImageAdjustRectComponent] | undefined => {
      return Array.from(watermarkComponentsRef.current.entries()).find(([, watermarkComponent]) => {
        return component === watermarkComponent;
      });
    };

    const handleClick = (event: MouseEvent) => {
      const hoveredComponent = renderer.findComponentUnderMouse(event);
      const hoveredComponentPath = Webgl2New.Webgl2dRenderer.global.getRootParent(hoveredComponent);
      const isSelectionComponentHit = hoveredComponentPath.some((component) => {
        return component === renderer.selectionComponent;
      });
      const selectedComponent = isSelectionComponentHit
        ? renderer.selectedComponent
        : hoveredComponent;
      const watermarkEntry = getWatermarkEntry(selectedComponent);

      if (!watermarkEntry) {
        setSelectedWatermarkId("");
        setSelectedLogoWatermarkId("");
        return;
      }

      const [watermarkId, component] = watermarkEntry;
      renderer.selectComponent(component);

      if (textWatermarks.some((watermark) => watermark.id === watermarkId)) {
        setMode("text");
        setSelectedLogoWatermarkId("");
        setSelectedWatermarkId(watermarkId);
        focusTextInput();
        return;
      }

      if (logoWatermarks.some((watermark) => watermark.id === watermarkId)) {
        setMode("logo");
        setSelectedWatermarkId("");
        setSelectedLogoWatermarkId(watermarkId);
      }
    };

    renderer.canvas.addEventListener("click", handleClick);
    return () => {
      renderer.canvas.removeEventListener("click", handleClick);
    };
  }, [focusTextInput, logoWatermarks, textWatermarks]);

  const handleSelectMode = (nextMode: WatermarkMode) => () => setMode(nextMode);

  const applyTextWatermarkToComponent = (
    watermark: TextWatermark,
    component: Webgl2New.ImageAdjustRectComponent,
    transform = component.transform,
  ) => {
    const canvas = createTextWatermarkCanvas(watermark);
    if (!canvas) return watermark;

    const texture = new Webgl2New.Texture();
    texture.loadSyncSource(canvas);
    component.state.texture = texture;
    component.state.filters.u_opacity = watermark.opacity / 100;
    component.transform.copy(transform);
    editMediaStore.renderer.selectionComponent.copyComponentTransform(component);
    editMediaStore.renderer.render();

    return getSyncedWatermarkFromComponent(
      {
        ...watermark,
        dimensions: {
          width: canvas.width,
          height: canvas.height,
        },
      },
      component,
    );
  };

  const getNextWatermarkTransform = (
    watermark: TextWatermark,
    component: Webgl2New.ImageAdjustRectComponent,
  ) => {
    const canvas = createTextWatermarkCanvas(watermark);
    if (!canvas) return component.transform.clone();

    const position = getComponentCenterPosition(component);
    const rotationAngle = component.transform.degrees;
    const scaleX = component.transform.size.width / Math.max(watermark.dimensions.width, 1);
    const scaleY = component.transform.size.height / Math.max(watermark.dimensions.height, 1);
    const nextTransform = component.transform.clone();

    nextTransform.calcDimensions({
      position,
      size: new Vec2(canvas.width * scaleX, canvas.height * scaleY),
      rotation: rotationAngle,
    });

    return nextTransform;
  };

  const createTextWatermarkComponent = (watermark: TextWatermark) => {
    const renderer = editMediaStore.renderer;
    const canvas = createTextWatermarkCanvas(watermark);

    if (!renderer || !canvas) return null;

    const texture = new Webgl2New.Texture();
    texture.loadSyncSource(canvas);

    const component = new Webgl2New.ImageAdjustRectComponent(texture, {
      u_opacity: watermark.opacity / 100,
    });
    const sourceTopOffset = toWatermarkSourcePx(initialWatermarkTopOffset);
    const position = new Vec2(
      renderer.canvasTranslation.x + renderer.canvasSize.width / 2,
      renderer.canvasTranslation.y + sourceTopOffset + canvas.height / 2,
    );

    component.transform.calcDimensions({
      position,
      size: new Vec2(canvas.width, canvas.height),
      rotation: watermark.rotationAngle,
    });

    return {
      component,
      watermark: {
        ...watermark,
        position: {
          x: position.x,
          y: position.y,
        },
        dimensions: {
          width: canvas.width,
          height: canvas.height,
        },
      },
    };
  };

  const createLogoWatermarkComponent = (image: HTMLImageElement, opacity: number) => {
    const renderer = editMediaStore.renderer;
    if (!renderer) return null;

    const texture = new Webgl2New.Texture();
    texture.loadSyncSource(image);

    const component = new Webgl2New.ImageAdjustRectComponent(texture, {
      u_opacity: opacity / 100,
    });
    const maxLogoWidth = renderer.canvasSize.width * 0.32;
    const scale = Math.min(1, maxLogoWidth / Math.max(image.width, 1));
    const width = Math.max(1, image.width * scale);
    const height = Math.max(1, image.height * scale);
    const sourceTopOffset = toWatermarkSourcePx(initialWatermarkTopOffset);
    const position = new Vec2(
      renderer.canvasTranslation.x + renderer.canvasSize.width / 2,
      renderer.canvasTranslation.y + sourceTopOffset + height / 2,
    );

    component.transform.calcDimensions({
      position,
      size: new Vec2(width, height),
    });

    return component;
  };

  const handleSelectWatermark = (id: string) => () => {
    setSelectedWatermarkId(id);
    editMediaStore.renderer.selectComponent(watermarkComponentsRef.current.get(id));
  };

  const handleWatermarkKeyDown = (id: string) => (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    setSelectedWatermarkId(id);
    editMediaStore.renderer.selectComponent(watermarkComponentsRef.current.get(id));
  };

  const updateSelectedWatermark = (data: Partial<TextWatermark>) => {
    if (!selectedWatermarkFromList) {
      setDraftWatermark((watermark) => ({ ...watermark, ...data }));
      return;
    }

    const component = watermarkComponentsRef.current.get(selectedWatermarkId);
    if (!component) return;

    const undoWatermark = getSyncedWatermarkFromComponent(selectedWatermarkFromList, component);
    const undoTransform = component.transform.clone();
    const actionWatermark = { ...undoWatermark, ...data };
    const actionTransform = getNextWatermarkTransform(actionWatermark, component);

    editMediaStore.renderer.commands.execute(
      new CommandAction(
        "text-watermark-update",
        () => {
          const nextWatermark = applyTextWatermarkToComponent(
            actionWatermark,
            component,
            actionTransform,
          );
          setTextWatermarks((watermarks) =>
            watermarks.map((watermark) =>
              watermark.id === selectedWatermarkId ? nextWatermark : watermark,
            ),
          );
          setSelectedWatermarkId(selectedWatermarkId);
        },
        () => {
          const previousWatermark = applyTextWatermarkToComponent(
            undoWatermark,
            component,
            undoTransform,
          );
          setTextWatermarks((watermarks) =>
            watermarks.map((watermark) =>
              watermark.id === selectedWatermarkId ? previousWatermark : watermark,
            ),
          );
          setSelectedWatermarkId(selectedWatermarkId);
        },
      ),
    );
  };

  const handleTextChange = (value: string) => {
    updateSelectedWatermark({ text: value });
  };

  const handleInputFocus = (value: string) => () => {
    focusedInputValueRef.current = value;
  };

  const handleTextBlur = () => {
    if (selectedWatermark.text.trim() || !focusedInputValueRef.current.trim()) return;
    updateSelectedWatermark({ text: focusedInputValueRef.current });
  };

  const handleColorPickerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateSelectedWatermark({ color: event.target.value.toUpperCase() });
  };

  const handleColorTextChange = (value: string) => {
    updateSelectedWatermark({ color: value.toUpperCase() });
  };

  const handleColorBlur = () => {
    if (hexColorRegExp.test(selectedWatermark.color)) return;
    updateSelectedWatermark({
      color: hexColorRegExp.test(focusedInputValueRef.current)
        ? focusedInputValueRef.current.toUpperCase()
        : defaultDraftWatermark.color,
    });
  };

  const handleSelectChange =
    (key: keyof Pick<TextWatermark, "font" | "weight">) => (option?: { value: string }) => {
      if (!option?.value) return;
      updateSelectedWatermark({ [key]: option.value });
    };

  const handleAlignmentChange = (alignment: TextWatermarkAlignment) => () => {
    updateSelectedWatermark({ alignment });
    setIsAlignmentMenuOpen(false);
  };

  const handleAlignmentMenuToggle = () => {
    setIsAlignmentMenuOpen((isOpen) => !isOpen);
  };

  const handleFontSizeChange = (value: number) => {
    const nextSize = String(clampFontSize(value));

    if (selectedWatermark.size === nextSize) return;
    updateSelectedWatermark({ size: nextSize });
  };

  const handleDecreaseFontSize = () => handleFontSizeChange(selectedFontSize - fontSizeStep);
  const handleIncreaseFontSize = () => handleFontSizeChange(selectedFontSize + fontSizeStep);

  const handleOpacityChange = (value: number) => {
    updateSelectedWatermark({ opacity: value });
  };

  const loadLogoImage = (file: File) => {
    const imageUrl = URL.createObjectURL(file);

    return new Promise<{ image: HTMLImageElement; imageUrl: string }>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve({ image, imageUrl });
      image.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error("Failed to load logo image"));
      };
      image.src = imageUrl;
    });
  };

  const getLogoDisplayName = (file: File) => {
    return file.name.replace(/\.[^.]+$/, "").trim() || "Logo";
  };

  const handleLogoUpload = async (files: File[]) => {
    const acceptedFiles = files.filter((file) =>
      ["image/png", "image/svg+xml", "image/jpeg"].includes(file.type),
    );

    await Promise.all(
      acceptedFiles.map(async (file) => {
        const { image, imageUrl } = await loadLogoImage(file);
        const logoWatermark: LogoWatermark = {
          id: `logo-watermark-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          name: getLogoDisplayName(file),
          imageUrl,
          opacity: 100,
        };
        const component = createLogoWatermarkComponent(image, logoWatermark.opacity);

        if (!component) {
          URL.revokeObjectURL(imageUrl);
          return;
        }

        editMediaStore.renderer.commands.execute(
          new CommandAction(
            "logo-watermark-add",
            () => {
              watermarkComponentsRef.current.set(logoWatermark.id, component);
              editMediaStore.renderer.addComponent(component);
              editMediaStore.renderer.selectComponent(component);
              setLogoWatermarks((watermarks) => {
                if (watermarks.some((item) => item.id === logoWatermark.id)) return watermarks;
                return [...watermarks, logoWatermark];
              });
              setSelectedLogoWatermarkId(logoWatermark.id);
            },
            () => {
              editMediaStore.renderer.removeComponent(component);
              watermarkComponentsRef.current.delete(logoWatermark.id);
              setLogoWatermarks((watermarks) =>
                watermarks.filter((item) => item.id !== logoWatermark.id),
              );
              setSelectedLogoWatermarkId("");
              editMediaStore.renderer.selectComponent(null);
            },
          ),
        );
      }),
    );
  };

  const bringWatermarkToFront = (
    id: string,
    component = watermarkComponentsRef.current.get(id),
  ) => {
    if (!component) return;

    const rendererComponents = editMediaStore.renderer.components as any[];
    const componentIndex = rendererComponents.findIndex((item) => item === component);

    if (componentIndex !== -1) {
      rendererComponents.splice(componentIndex, 1);
      rendererComponents.push(component);
    }

    watermarkComponentsRef.current.delete(id);
    watermarkComponentsRef.current.set(id, component);
    editMediaStore.renderer.selectComponent(component);
  };

  const handleSelectLogoWatermark = (id: string) => () => {
    setSelectedLogoWatermarkId(id);
    bringWatermarkToFront(id);
  };

  const handleLogoWatermarkKeyDown =
    (id: string) => (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      setSelectedLogoWatermarkId(id);
      bringWatermarkToFront(id);
    };

  const handleLogoOpacityChange = (value: number) => {
    if (!selectedLogoWatermark) return;

    const component = watermarkComponentsRef.current.get(selectedLogoWatermark.id);
    if (!component) return;

    component.state.filters.u_opacity = value / 100;
    editMediaStore.renderer.render();
    setLogoWatermarks((watermarks) =>
      watermarks.map((watermark) =>
        watermark.id === selectedLogoWatermark.id ? { ...watermark, opacity: value } : watermark,
      ),
    );
  };

  const handleDeleteWatermark = (id: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const component = watermarkComponentsRef.current.get(id);
    const deletedWatermark = textWatermarks.find((watermark) => watermark.id === id);
    const deletedWatermarkIndex = textWatermarks.findIndex((watermark) => watermark.id === id);

    if (!component || !deletedWatermark) return;

    editMediaStore.renderer.commands.execute(
      new CommandAction(
        "text-watermark-delete",
        () => {
          editMediaStore.renderer.removeComponent(component);
          watermarkComponentsRef.current.delete(id);

          setTextWatermarks((watermarks) => {
            const nextWatermarks = watermarks.filter((watermark) => watermark.id !== id);
            if (id === selectedWatermarkId) {
              const nextSelectedWatermarkId = nextWatermarks[0]?.id ?? "";
              setSelectedWatermarkId(nextSelectedWatermarkId);
              editMediaStore.renderer.selectComponent(
                nextSelectedWatermarkId
                  ? watermarkComponentsRef.current.get(nextSelectedWatermarkId)
                  : null,
              );
            }
            return nextWatermarks;
          });
        },
        () => {
          watermarkComponentsRef.current.set(id, component);
          editMediaStore.renderer.addComponent(component);
          editMediaStore.renderer.selectComponent(component);

          setTextWatermarks((watermarks) => {
            if (watermarks.some((watermark) => watermark.id === id)) return watermarks;

            const nextWatermarks = [...watermarks];
            nextWatermarks.splice(deletedWatermarkIndex, 0, deletedWatermark);
            return nextWatermarks;
          });
          setSelectedWatermarkId(id);
        },
      ),
    );
  };

  const handleDeleteLogoWatermark =
    (id: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const component = watermarkComponentsRef.current.get(id);
      const deletedWatermark = logoWatermarks.find((watermark) => watermark.id === id);
      const deletedWatermarkIndex = logoWatermarks.findIndex((watermark) => watermark.id === id);

      if (!component || !deletedWatermark) return;

      editMediaStore.renderer.commands.execute(
        new CommandAction(
          "logo-watermark-delete",
          () => {
            editMediaStore.renderer.removeComponent(component);
            watermarkComponentsRef.current.delete(id);

            setLogoWatermarks((watermarks) => {
              const nextWatermarks = watermarks.filter((watermark) => watermark.id !== id);
              if (id === selectedLogoWatermarkId) {
                const nextSelectedWatermarkId = nextWatermarks[0]?.id ?? "";
                setSelectedLogoWatermarkId(nextSelectedWatermarkId);
                editMediaStore.renderer.selectComponent(
                  nextSelectedWatermarkId
                    ? watermarkComponentsRef.current.get(nextSelectedWatermarkId)
                    : null,
                );
              }
              return nextWatermarks;
            });
          },
          () => {
            watermarkComponentsRef.current.set(id, component);
            editMediaStore.renderer.addComponent(component);
            editMediaStore.renderer.selectComponent(component);

            setLogoWatermarks((watermarks) => {
              if (watermarks.some((watermark) => watermark.id === id)) return watermarks;

              const nextWatermarks = [...watermarks];
              nextWatermarks.splice(deletedWatermarkIndex, 0, deletedWatermark);
              return nextWatermarks;
            });
            setSelectedLogoWatermarkId(id);
          },
        ),
      );
    };

  const handleAddTextWatermark = () => {
    const text = draftWatermark.text.trim();
    if (!text) return;

    const createdWatermark = createTextWatermarkComponent({
      ...draftWatermark,
      id: `watermark-${Date.now()}`,
      text,
    });

    if (!createdWatermark) return;

    const { component, watermark } = createdWatermark;

    editMediaStore.renderer.commands.execute(
      new CommandAction(
        "text-watermark-add",
        () => {
          watermarkComponentsRef.current.set(watermark.id, component);
          editMediaStore.renderer.addComponent(component);
          editMediaStore.renderer.selectComponent(component);
          setTextWatermarks((watermarks) => {
            if (watermarks.some((item) => item.id === watermark.id)) return watermarks;
            return [...watermarks, watermark];
          });
          setDraftWatermark(defaultDraftWatermark);
          setSelectedWatermarkId(watermark.id);
        },
        () => {
          editMediaStore.renderer.removeComponent(component);
          watermarkComponentsRef.current.delete(watermark.id);
          setTextWatermarks((watermarks) => watermarks.filter((item) => item.id !== watermark.id));
          setDraftWatermark(defaultDraftWatermark);
          setSelectedWatermarkId("");
          editMediaStore.renderer.selectComponent(null);
        },
      ),
    );
  };

  const handleConfirmUpdateTextWatermark = () => {
    setDraftWatermark(defaultDraftWatermark);
    setSelectedWatermarkId("");
  };

  const closeWatermarkPanel = () => {
    editMediaStore.openAdjustmentsTab();
    editMediaStore.renderer.selectComponent(editMediaStore.mainImageComponent);
  };

  const removeWatermarkComponents = (
    components: Webgl2New.ImageAdjustRectComponent[] = Array.from(
      watermarkComponentsRef.current.values(),
    ),
  ) => {
    components.forEach((component) => {
      if (editMediaStore.renderer.findComponent(component as any)) {
        editMediaStore.renderer.removeComponent(component);
      }
    });
    editMediaStore.renderer.selectComponent(null);
    editMediaStore.renderer.render();
  };

  const resetWatermarkState = () => {
    watermarkComponentsRef.current.clear();
    setTextWatermarks(defaultTextWatermarks);
    setLogoWatermarks(defaultLogoWatermarks);
    setDraftWatermark(defaultDraftWatermark);
    setSelectedWatermarkId("");
    setSelectedLogoWatermarkId("");
  };

  const discardPendingWatermarkCommands = () => {
    while (editMediaStore.renderer.commands.currentHistory > initialCommandHistoryRef.current) {
      editMediaStore.renderer.commands.undo();
    }

    editMediaStore.renderer.commands.clearRedo();
  };

  const createTextWatermarkedTexture = (
    mainImageComponent: Webgl2New.ImageAdjustRectComponent,
    watermarkComponents: Webgl2New.ImageAdjustRectComponent[],
  ) => {
    editMediaStore.renderer.render();

    const baseCanvas = mainImageComponent.state.texture.textureToCanvas();
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) return null;

    canvas.width = baseCanvas.width;
    canvas.height = baseCanvas.height;
    context.drawImage(baseCanvas, 0, 0);

    const mainModelMatrix = Mat3.identity().copy(mainImageComponent.getModelMatrix());
    const mainModelMatrixInverted = mainModelMatrix.clone().invert();
    const getMainTexturePosition = (
      component: Webgl2New.ImageAdjustRectComponent,
      sourcePosition: Vec2,
      sourceSize: Vec2,
    ) => {
      const sourceUnitPosition = sourcePosition.clone().divide(sourceSize);
      const globalPosition = sourceUnitPosition.transformMat3(component.getModelMatrix());
      const mainUnitPosition = globalPosition.transformMat3(mainModelMatrixInverted.mat);

      return mainUnitPosition.multiply(baseCanvas.width, baseCanvas.height);
    };

    watermarkComponents.forEach((component) => {
      const watermarkCanvas = component.state.texture.textureToCanvas();
      if (!watermarkCanvas.width || !watermarkCanvas.height) return;

      const sourceSize = new Vec2(watermarkCanvas.width, watermarkCanvas.height);
      const origin = getMainTexturePosition(component, Vec2.zero(), sourceSize);
      const horizontal = getMainTexturePosition(
        component,
        new Vec2(watermarkCanvas.width, 0),
        sourceSize,
      );
      const vertical = getMainTexturePosition(
        component,
        new Vec2(0, watermarkCanvas.height),
        sourceSize,
      );

      context.save();
      context.globalAlpha = component.state.filters.u_opacity;
      context.setTransform(
        (horizontal.x - origin.x) / watermarkCanvas.width,
        (horizontal.y - origin.y) / watermarkCanvas.width,
        (vertical.x - origin.x) / watermarkCanvas.height,
        (vertical.y - origin.y) / watermarkCanvas.height,
        origin.x,
        origin.y,
      );
      context.drawImage(watermarkCanvas, 0, 0);
      context.restore();
    });

    const texture = new Webgl2New.Texture();
    texture.loadSyncSource(canvas);
    return texture;
  };

  const handleCancel = (onCancel?: VoidFunction) => {
    editMediaStore.unregisterTabBlockCallback();
    discardPendingWatermarkCommands();

    removeWatermarkComponents();
    resetWatermarkState();
    closeWatermarkPanel();
    onCancel?.();
  };

  const handleDone = (onDone?: VoidFunction) => {
    editMediaStore.unregisterTabBlockCallback();

    const mainImageComponent = editMediaStore.mainImageComponent;
    const watermarkComponents = Array.from(watermarkComponentsRef.current.values()).filter(
      (component) => !!editMediaStore.renderer.findComponent(component as any),
    );

    if (!mainImageComponent || !watermarkComponents.length) {
      discardPendingWatermarkCommands();
      resetWatermarkState();
      closeWatermarkPanel();
      onDone?.();
      return;
    }

    const undoTexture = mainImageComponent.state.texture.clone();
    const actionTexture = createTextWatermarkedTexture(mainImageComponent, watermarkComponents);

    if (!actionTexture) return;
    discardPendingWatermarkCommands();

    editMediaStore.renderer.commands.execute(
      new CommandAction(
        "text-watermark-apply",
        () => {
          mainImageComponent.state.texture = actionTexture.clone();
          removeWatermarkComponents(watermarkComponents);
          editMediaStore.renderer.selectComponent(mainImageComponent);
        },
        () => {
          mainImageComponent.state.texture = undoTexture.clone();
          removeWatermarkComponents(watermarkComponents);
          editMediaStore.renderer.selectComponent(mainImageComponent);
        },
      ),
    );

    resetWatermarkState();
    closeWatermarkPanel();
    onDone?.();
  };

  useEffect(() => {
    return editMediaStore.registerTabBlockCallback(({ unregister, proceed }) => {
      if (!hasPendingWatermarkChanges) {
        unregister();
        return;
      }

      unsavedChangesNotification(() => {
        unregister();
        handleCancel(proceed);
      });

      return true;
    });
  }, [hasPendingWatermarkChanges]);

  return (
    <section className={styles.watermarkPanel}>
      <Typography size="small600" color="textSecondary">
        {localeStore.t('["edit-media"].inspector.watermark.title')}
      </Typography>

      <div className={styles.modeTabs} role="tablist">
        <Button
          type="button"
          appearance="unset"
          size="unset"
          className={clsx(styles.modeTab, { [styles.active]: mode === "text" })}
          onClick={handleSelectMode("text")}
        >
          {localeStore.t('["edit-media"].inspector.watermark.text')}
        </Button>
        <Button
          type="button"
          appearance="unset"
          size="unset"
          className={clsx(styles.modeTab, { [styles.active]: mode === "logo" })}
          onClick={handleSelectMode("logo")}
        >
          {localeStore.t('["edit-media"].inspector.watermark.logo')}
        </Button>
      </div>

      {mode === "text" && (
        <>
          <div className={clsx(styles.watermarkList, globalStyles.addScrollStyles)}>
            {textWatermarks.map((watermark) => {
              const isSelected = watermark.id === selectedWatermarkId;

              return (
                <div
                  key={watermark.id}
                  role="button"
                  tabIndex={0}
                  className={clsx(styles.watermarkItem, { [styles.selected]: isSelected })}
                  onClick={handleSelectWatermark(watermark.id)}
                  onKeyDown={handleWatermarkKeyDown(watermark.id)}
                >
                  <span className={styles.textIcon}>T</span>
                  <span className={styles.watermarkItemText}>{watermark.text}</span>
                  <Button
                    className={styles.deleteButton}
                    appearance="errorGhost"
                    size="small"
                    onClick={handleDeleteWatermark(watermark.id)}
                    aria-label={localeStore.t("common.buttons.delete")}
                  >
                    <SvgIcon icon={TrashSVG} />
                  </Button>
                </div>
              );
            })}
          </div>

          <label className={styles.field}>
            <span>{localeStore.t('["edit-media"].inspector.watermark.text')}</span>
            <Input
              type="textarea"
              className={styles.textarea}
              inputWrapperClassName={styles.textareaInputWrapper}
              inputClassName={styles.textareaInput}
              appearance="primaryV2"
              value={selectedWatermark?.text}
              onChange={handleTextChange}
              onFocus={handleInputFocus(selectedWatermark.text)}
              onBlur={handleTextBlur}
              forwardRef={textInputRef}
              disableError
              maxLength={80}
              placeholder={localeStore.t('["edit-media"].inspector.watermark["text-placeholder"]')}
            />
          </label>

          {isEditingDraft ? (
            canAddTextWatermark && (
              <Button className={styles.addButton} onClick={handleAddTextWatermark}>
                {localeStore.t('["edit-media"].inspector.watermark["add-text"]')}
              </Button>
            )
          ) : (
            <Button className={styles.addButton} onClick={handleConfirmUpdateTextWatermark}>
              {localeStore.t('["edit-media"].inspector.watermark["update-text"]')}
            </Button>
          )}

          <div className={styles.fontAlignmentFields}>
            <label className={styles.field}>
              <span>{localeStore.t('["edit-media"].inspector.watermark.font')}</span>
              <Select
                className={styles.select}
                inputWrapperClassName={styles.selectInput}
                appearance="primaryV2"
                selectedOptionsKeys={selectedWatermark?.font}
                options={fontSelectOptions}
                onChange={handleSelectChange("font")}
                disableClearing
                disableError
              />
            </label>

            <div className={styles.field} ref={alignmentMenuRef}>
              <span>
                {localeStore.t(
                  '["edit-media"].inspector.watermark.alignment.label' as any,
                  "Alignment",
                )}
              </span>
              <div className={styles.alignmentSelect}>
                <button
                  className={styles.alignmentSelectInput}
                  type="button"
                  onClick={handleAlignmentMenuToggle}
                  aria-expanded={isAlignmentMenuOpen}
                  aria-label={getAlignmentLabel(selectedWatermark.alignment)}
                  title={getAlignmentLabel(selectedWatermark.alignment)}
                >
                  {renderAlignmentIcon(selectedWatermark.alignment)}
                  <span className={styles.alignmentCaret} aria-hidden="true" />
                </button>

                {isAlignmentMenuOpen && (
                  <div className={styles.alignmentDropdown} role="menu">
                    {alignmentOptions.map((alignment) => {
                      const isSelected = selectedWatermark.alignment === alignment;

                      return (
                        <button
                          key={alignment}
                          className={getAlignmentButtonClassName(alignment, isSelected)}
                          type="button"
                          onClick={handleAlignmentChange(alignment)}
                          aria-pressed={isSelected}
                          aria-label={getAlignmentLabel(alignment)}
                          title={getAlignmentLabel(alignment)}
                          role="menuitem"
                        >
                          <span />
                          <span />
                          <span />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.twoColumnFields}>
            <div className={clsx(styles.field, styles.sizeField)}>
              <span>{localeStore.t('["edit-media"].inspector.watermark.size')}</span>
              <div className={styles.sizeStepper}>
                <button
                  className={styles.sizeStepperButton}
                  type="button"
                  onClick={handleDecreaseFontSize}
                  disabled={selectedFontSize <= fontSizeMin}
                  aria-label={localeStore.t(
                    '["edit-media"].inspector.watermark["decrease-font-size"]',
                  )}
                >
                  -
                </button>
                <span className={styles.sizeStepperValue}>{selectedFontSize}</span>
                <button
                  className={styles.sizeStepperButton}
                  type="button"
                  onClick={handleIncreaseFontSize}
                  disabled={selectedFontSize >= fontSizeMax}
                  aria-label={localeStore.t(
                    '["edit-media"].inspector.watermark["increase-font-size"]',
                  )}
                >
                  +
                </button>
              </div>
            </div>

            <label className={clsx(styles.field, styles.weightField)}>
              <span>{localeStore.t('["edit-media"].inspector.watermark.weight')}</span>
              <Select
                className={styles.select}
                inputWrapperClassName={styles.selectInput}
                appearance="primaryV2"
                selectedOptionsKeys={selectedWatermark?.weight}
                options={weightSelectOptions}
                onChange={handleSelectChange("weight")}
                disableClearing
                disableError
              />
            </label>
          </div>

          <label className={styles.field}>
            <span>{localeStore.t('["edit-media"].inspector.watermark.color')}</span>
            <div className={styles.colorControl}>
              <input
                className={styles.colorPicker}
                type="color"
                value={selectedWatermark?.color}
                onChange={handleColorPickerChange}
                aria-label={localeStore.t('["edit-media"].inspector.watermark.color')}
              />
              <Input
                inputWrapperClassName={styles.colorTextWrapper}
                inputClassName={styles.colorTextInput}
                appearance="primaryV2"
                value={selectedWatermark?.color}
                onChange={handleColorTextChange}
                onFocus={handleInputFocus(selectedWatermark.color)}
                onBlur={handleColorBlur}
                disableError
              />
            </div>
          </label>

          <Range
            label={localeStore.t('["edit-media"].inspector.watermark.opacity')}
            min={0}
            max={100}
            step={1}
            value={selectedWatermark?.opacity}
            onChange={handleOpacityChange}
            messageFormatter={(value) => `${value}%`}
            disableError
            showRangeValue
          />
        </>
      )}

      {mode === "logo" && (
        <>
          {logoWatermarks.length > 0 && (
            <div className={clsx(styles.logoList, globalStyles.addScrollStyles)}>
              {logoWatermarks.map((watermark) => {
                const isSelected = watermark.id === selectedLogoWatermarkId;

                return (
                  <div
                    key={watermark.id}
                    role="button"
                    tabIndex={0}
                    className={clsx(styles.logoItem, { [styles.selected]: isSelected })}
                    onClick={handleSelectLogoWatermark(watermark.id)}
                    onKeyDown={handleLogoWatermarkKeyDown(watermark.id)}
                  >
                    <span className={styles.logoPreview}>
                      <img src={watermark.imageUrl} alt="" />
                    </span>
                    <span className={styles.logoName}>{watermark.name}</span>
                    <Button
                      className={styles.deleteButton}
                      appearance="errorGhost"
                      size="small"
                      onClick={handleDeleteLogoWatermark(watermark.id)}
                      aria-label={getDeleteLogoLabel(watermark.name)}
                    >
                      <SvgIcon icon={TrashSVG} />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          <label className={styles.field}>
            <span>{localeStore.t('["edit-media"].inspector.watermark["upload-logo"]')}</span>
            <Dropzone
              accept="image/png, image/svg+xml, image/jpeg, image/jpg"
              onDropAccepted={handleLogoUpload}
              preventDropOnDocument
            >
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps({}, true)}
                  className={styles.logoUpload}
                  role="button"
                  tabIndex={0}
                >
                  <input {...getInputProps()} />
                  <SvgIcon className={styles.logoUploadIcon} icon={UploadSVG} size={32} />
                  <span className={styles.logoUploadTitle}>
                    {localeStore.t(
                      '["edit-media"].inspector.watermark["drag-or-click-upload-logo"]',
                    )}
                  </span>
                  <span className={styles.logoUploadSubtitle}>
                    {localeStore.t('["edit-media"].inspector.watermark["supported-logo-formats"]')}
                  </span>
                </div>
              )}
            </Dropzone>
          </label>

          <Range
            label={localeStore.t('["edit-media"].inspector.watermark.opacity')}
            min={0}
            max={100}
            step={1}
            value={selectedLogoWatermark?.opacity ?? 100}
            onChange={handleLogoOpacityChange}
            messageFormatter={(value) => `${value}%`}
            disableError
            showRangeValue
          />
        </>
      )}

      <div className={styles.actions}>
        <Button appearance="secondaryOutlined" onClick={() => handleCancel()}>
          {localeStore.t("common.buttons.cancel")}
        </Button>
        <Button onClick={() => handleDone()} disabled={!canApplyWatermark}>
          {localeStore.t("common.buttons.done")}
        </Button>
      </div>
    </section>
  );
});

export default Watermark;
